import Enumerable from "linq";
import { LineStyle, OptionalFeature } from "../../../../base/domain/drawing";
import { ValueRange } from "../../../../base/domain/system";
import { ILineGraphBehavior, ILineGraphItem, ILineGraphPair, ILineGraphLinkedObjectSpecifiable } from "./ILineGraphBehavior";
import { GridBehavior } from "./GridBehavior";
import { LinkedObject } from "./LinkedObject";
import { TableIndex } from "./TableIndex";

export class LineGraphBehavior {
  constructor(
    public commonTimeAxis: OptionalFeature  = new OptionalFeature(),
    public border: OptionalFeature          = new OptionalFeature(),
    public horizontalGrid: GridBehavior     = new GridBehavior(),
    public items: LineGraphItem[]           = [],
  ) {
  }

  clone(): LineGraphBehavior {
    return new LineGraphBehavior(this.commonTimeAxis.clone(), this.border.clone(), this.horizontalGrid.clone(), this.items.concat());
  }

  static from(source: ILineGraphBehavior): LineGraphBehavior {
    return new LineGraphBehavior(
      OptionalFeature.from(source.commonTimeAxis),
      OptionalFeature.from(source.border),
      GridBehavior.from(source.horizontalGrid),
      Enumerable.from(source.items).select(i => LineGraphItem.from(i)).toArray());
  }

  static to(source: LineGraphBehavior): ILineGraphBehavior {
    return {
      commonTimeAxis: OptionalFeature.to(source.commonTimeAxis),
      border: OptionalFeature.to(source.border),
      horizontalGrid: GridBehavior.to(source.horizontalGrid),
      items: Enumerable.from(source.items).select(i => LineGraphItem.to(i)).toArray(),
    };
  }
}

export class LineGraphItem {
  constructor(
    public name: string,
    public range: ValueRange,
    public isTimeAxisDate: boolean,
    public lineStyle: LineStyle,
    public pairs: LineGraphPair[]
  ) {
  }

  clone(): LineGraphItem {
    return new LineGraphItem(
      this.name,
      this.range.clone(),
      this.isTimeAxisDate,
      this.lineStyle.clone(),
      this.pairs.concat());
  }

  static from(source: ILineGraphItem): LineGraphItem {
    return new LineGraphItem(
      source.name,
      ValueRange.from(source.range),
      source.isTimeAxisDate,
      LineStyle.from(source.lineStyle),
      Enumerable.from(source.pairs).select(v => LineGraphPair.from(v)).toArray());
  }

  static to(source: LineGraphItem): ILineGraphItem {
    return {
      name: source.name,
      range: ValueRange.to(source.range),
      isTimeAxisDate: source.isTimeAxisDate,
      lineStyle: LineStyle.to(source.lineStyle),
      pairs: Enumerable.from(source.pairs).select(v => LineGraphPair.to(v)).toArray(),
    };
  }
}

export class LineGraphPair {
  constructor(
    public timeAxis: LineGraphLinkedObject,
    public value: LineGraphLinkedObject,
  ) {
  }

  clone(): LineGraphPair {
    return new LineGraphPair(
      this.timeAxis.clone(),
      this.value.clone());
  }

  static from(source: ILineGraphPair): LineGraphPair {
    return new LineGraphPair(
      LineGraphLinkedObject.from(source.timeAxis),
      LineGraphLinkedObject.from(source.value));
  }

  static to(source: LineGraphPair): ILineGraphPair {
    return {
      timeAxis: LineGraphLinkedObject.to(source.timeAxis),
      value: LineGraphLinkedObject.to(source.value),
    };
  }
}

export class LineGraphLinkedObject extends LinkedObject {
  constructor(
    public page: number,
    type: string,
    id: number,
    key: string,
    subKey?: string,
    tableIndex?: TableIndex,
    isDate?: boolean,
  ) {
    super(type, id, key, subKey, tableIndex, isDate);
  }

  clone(): LineGraphLinkedObject {
    return new LineGraphLinkedObject(
      this.page,
      this.type,
      this.id,
      this.key,
      this.subKey,
      this.tableIndex.clone(),
      this.isDate);
  }

  static from(source: ILineGraphLinkedObjectSpecifiable): LineGraphLinkedObject {
    return new LineGraphLinkedObject(
      source.page,
      source.type,
      source.id,
      source.key,
      source.subKey,
      TableIndex.from(source.tableIndex),
      source.isDate);
  }

  static to(source: LineGraphLinkedObject): ILineGraphLinkedObjectSpecifiable {
    return {
      page: source.page,
      type: source.type,
      id: source.id,
      key: source.key,
      subKey: source.subKey,
      tableIndex: TableIndex.to(source.tableIndex),
      isDate: source.isDate,
    };
  }
}
